import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase, isSupabaseConfigured } from '../lib/supabase'
import { logger } from '../utils/logger'
import { useAuth } from './useAuth'

interface UseTokensReturn {
  balance: number
  loading: boolean
  error: string | null
  refreshBalance: () => Promise<void>
}

// Sistema de eventos global para sincronizar todos os hooks
class TokenBalanceEventEmitter {
  private listeners: Set<(balance: number) => void> = new Set()
  
  subscribe(callback: (balance: number) => void) {
    this.listeners.add(callback)
    return () => {
      this.listeners.delete(callback)
    }
  }
  
  emit(balance: number) {
    this.listeners.forEach(callback => callback(balance))
  }
}

const globalBalanceEmitter = new TokenBalanceEventEmitter()
let globalSubscription: any = null
let globalUserId: string | null = null
let globalBalance: number = 0

export function useTokensSimple(): UseTokensReturn {
  const { user } = useAuth()
  const [balance, setBalance] = useState(globalBalance)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const mountedRef = useRef(true)

  // Garantir que mountedRef seja sempre true quando o hook é executado
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Subscrever ao sistema de eventos global
  useEffect(() => {
    const unsubscribe = globalBalanceEmitter.subscribe((newBalance) => {
      if (mountedRef.current) {
        setBalance(newBalance)
        globalBalance = newBalance
      }
    })
    
    return unsubscribe
  }, [])

  const fetchBalance = useCallback(async () => {
    if (!user || !mountedRef.current) {
      const newBalance = 0
      setBalance(newBalance)
      globalBalance = newBalance
      globalBalanceEmitter.emit(newBalance)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      if (!isSupabaseConfigured) {
        const demoBalance = 150
        setBalance(demoBalance)
        globalBalance = demoBalance
        globalBalanceEmitter.emit(demoBalance)
        logger.warn('Demo mode: Using simulated token balance', { balance: demoBalance })
        return
      }

      const { data, error: fetchError } = await supabase
        .from('user_wallets')
        .select('token_balance')
        .eq('user_id', user.id)
        .maybeSingle()

      if (fetchError) {
        throw fetchError
      }

      if (mountedRef.current) {
        const newBalance = data?.token_balance || 0
        setBalance(newBalance)
        globalBalance = newBalance
        globalBalanceEmitter.emit(newBalance)
      }
    } catch (err: any) {
      if (mountedRef.current) {
        logger.error('Error fetching token balance', err)
        setError(err.message)
        const newBalance = 0
        setBalance(newBalance)
        globalBalance = newBalance
        globalBalanceEmitter.emit(newBalance)
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false)
      }
    }
  }, [user?.id])

  const refreshBalance = useCallback(async () => {
    await fetchBalance()
  }, [fetchBalance])

  // Fetch inicial - executar sempre que user mudar
  useEffect(() => {
    fetchBalance()
  }, [fetchBalance])

  // Subscription global única - só criar após fetch inicial
  useEffect(() => {
    if (!user || !isSupabaseConfigured || !mountedRef.current) {
      return
    }

    // Aguardar um pouco para garantir que o fetch inicial aconteceu
    const subscriptionTimer = setTimeout(() => {
      // Se já existe subscription para outro usuário, limpar
      if (globalSubscription && globalUserId !== user.id) {
        globalSubscription.unsubscribe()
        globalSubscription = null
        globalUserId = null
      }

      // Se já existe subscription para este usuário, não criar outra
      if (globalSubscription && globalUserId === user.id) {
        return
      }

      // logger.info('Creating GLOBAL token balance subscription', { userId: user.id })

      const channel = supabase
        .channel(`global_user_wallets_${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'user_wallets',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            const newBalance = payload.new.token_balance
            
            // logger.info('ðŸ’° GLOBAL Token balance updated!', { 
            //   userId: user.id, 
            //   newBalance,
            //   oldBalance: payload.old?.token_balance
            // })
            
            // Atualizar TODOS os componentes que usam este hook via evento global
            // console.log('ðŸŽ° Emitting balance update to all components:', { newBalance, userId: user.id })
            globalBalance = newBalance
            globalBalanceEmitter.emit(newBalance)
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            // logger.info('œ… GLOBAL Token balance subscription active', { userId: user.id })
          } else if (status === 'CHANNEL_ERROR') {
            logger.error('Œ GLOBAL Token balance subscription error', { userId: user.id })
          }
        })

      globalSubscription = channel
      globalUserId = user.id
    }, 1000) // Aguardar 1 segundo após o fetch inicial

    return () => {
      clearTimeout(subscriptionTimer)
      mountedRef.current = false
      // NÃO limpar a subscription global aqui - ela fica ativa
    }
  }, [user?.id])

  // Cleanup quando componente desmonta
  useEffect(() => {
    return () => {
      mountedRef.current = false
    }
  }, [])

  return {
    balance,
    loading,
    error,
    refreshBalance
  }
}
